import { isOpenPartnerProductOrderItemModal } from "@/atoms/item/is-open-partner-product-order-item-modal";
import { partnerProductOrderItemPageState } from "@/atoms/item/partner-product-order-item-page-state";
import { GLOBAL_ERROR } from "@/common/constant/global-error";
import { ButtonColor } from "@/common/enum/button-color";
import { itemService } from "@/services/item-service";
import { Button, Modal } from "flowbite-react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { ImageCarouselViewer } from "../carousel/ImageCarouselViewer";
import { StarOutlineIcon } from "../icon/StarOutlineIcon";
import { StarSolidIcon } from "../icon/StarSolidIcon";
import { Body14Medium } from "../typography/Body14Medium";
import { Footnote12Description } from "../typography/Footnote12Description";
import { H3 } from "../typography/H3";
import { H4 } from "../typography/H4";
import { H5 } from "../typography/H5";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import { useEffect } from "react";

const isJsonString = (str: string) => {
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
};

export const PartnerProductOrderItemDetailModal = () => {
  const [isOpen, setIsOpen] = useRecoilState(
    isOpenPartnerProductOrderItemModal
  );

  const setPage = useSetRecoilState(partnerProductOrderItemPageState);

  const handleCloseModal = () => {
    setIsOpen(undefined);
  };

  // TODO 즐겨찾기
  const handleFavoriteButtonClick = async () => {
    if (!isOpen) {
      return;
    }
    try {
      const response = isOpen.isStar
        ? await itemService.deleteStar(isOpen.id)
        : await itemService.star(isOpen.id);

      if (!response.ok) {
        return alert(GLOBAL_ERROR);
      }

      const newItem = await itemService.getById({ id: isOpen.id });
      if (!newItem.ok) {
        return alert(GLOBAL_ERROR);
      }

      setPage((prev) => ({
        ...prev,
        contents: prev.contents.map((item) =>
          item.id === newItem.data.id ? newItem.data : item
        ),
      }));
      setIsOpen(newItem.data);
    } catch (error) {
      console.log(error);
    }
  };

  const detailsEditor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: true,
        autolink: true,
        defaultProtocol: "https",
        HTMLAttributes: {
          class: "custom-link",
        },
      }),
      Underline,
      Image.configure({
        HTMLAttributes: {
          class: "custom-image",
        },
      }),
    ],
    content: {},
    editable: false,
  });

  const featuresEditor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: true,
        autolink: true,
        defaultProtocol: "https",
        HTMLAttributes: {
          class: "custom-link",
        },
      }),
      Underline,
      Image.configure({
        HTMLAttributes: {
          class: "custom-image",
        },
      }),
    ],
    content: {},
    editable: false,
  });

  useEffect(() => {
    if (isOpen) {
      try {
        if (detailsEditor) {
          try {
            if (isOpen.details && isJsonString(isOpen.details)) {
              const detailsContent = JSON.parse(isOpen.details);
              detailsEditor.commands.setContent(detailsContent);
            } else {
              detailsEditor.commands.setContent({
                type: "doc",
                content: [
                  {
                    type: "paragraph",
                    content: [{ type: "text", text: isOpen.details || "" }],
                  },
                ],
              });
            }
          } catch (error) {
            console.error("Details processing error:", error);
          }
        }

        if (featuresEditor) {
          try {
            if (isOpen.features && isJsonString(isOpen.features)) {
              const featuresContent = JSON.parse(isOpen.features);
              featuresEditor.commands.setContent(featuresContent);
            } else {
              featuresEditor.commands.setContent({
                type: "doc",
                content: [
                  {
                    type: "paragraph",
                    content: [{ type: "text", text: isOpen.features || "" }],
                  },
                ],
              });
            }
          } catch (error) {
            console.error("Features processing error:", error);
          }
        }
      } catch (error) {
        console.error("Overall error:", error);
      }
    }
  }, [isOpen, detailsEditor, featuresEditor]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal size="7xl" show={true} onClose={handleCloseModal}>
      <Modal.Header>
        <H4 className="text-semantic-text-heading">상품 상세 정보</H4>
      </Modal.Header>
      <Modal.Body>
        <div className="flex">
          <ImageCarouselViewer images={isOpen.imageUrls} />
          <div className="flex flex-col ml-[20px] max-w-[412px] whitespace-break-spaces ">
            <Footnote12Description className="text-semantic-text-tertiary mb-[4px] max-w-[412px] whitespace-break-spaces">
              {isOpen.category.name}
            </Footnote12Description>
            <div className="flex mb-[16px]">
              <H3 className="text-semantic-text-heading max-w-[412px] whitespace-break-spaces">
                {isOpen.name}
              </H3>
              <Footnote12Description className="text-semantic-text-tertiary ml-[4px]">
                {/* model name */}
                {isOpen.modelName}
              </Footnote12Description>
            </div>
            <div className="text-semantic-text-body mb-[16px] max-w-[412px] whitespace-break-spaces">
              <EditorContent
                editor={detailsEditor}
                className="[&_.ProseMirror]:!min-h-0 [&_.ProseMirror]:!h-auto"
              />
            </div>
            <H5 className="text-semantic-text-body mb-[8px]">기능</H5>
            <div className="max-w-[412px] whitespace-break-spaces">
              <EditorContent
                editor={featuresEditor}
                className="[&_.ProseMirror]:!min-h-0 [&_.ProseMirror]:!h-auto"
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          color={ButtonColor.PRIMARY_OUTLINE}
          onClick={handleFavoriteButtonClick}
          className="w-[150px] h-[40px]"
        >
          {isOpen.isStar ? (
            <StarSolidIcon size={20} color="text-secondary-2-orange" />
          ) : (
            <StarOutlineIcon size={20} color="text-grayscale-contrast-80" />
          )}

          <Body14Medium className="text-semantic-text-body ml-[8px]">
            상품 즐겨찾기
          </Body14Medium>
        </Button>
        {isOpen.link && (
          <Button
            color={ButtonColor.PRIMARY_OUTLINE}
            onClick={() => window.open(isOpen.link, "_blank")}
            className="w-[150px] h-[40px]"
          >
            <svg
              className="w-6 h-6 text-gray-800 dark:text-white"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13.213 9.787a3.391 3.391 0 0 0-4.795 0l-3.425 3.426a3.39 3.39 0 0 0 4.795 4.794l.321-.304m-.321-4.49a3.39 3.39 0 0 0 4.795 0l3.424-3.426a3.39 3.39 0 0 0-4.794-4.795l-1.028.961"
              />
            </svg>

            <Body14Medium className="text-semantic-text-body ml-[8px]">
              더 많은 정보 보기
            </Body14Medium>
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
